import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { createColumnHelper } from '@tanstack/react-table';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CloseButton from 'components/shared/CloseButton';
import DataTable from 'components/shared/DataTable';
import Foldout from 'components/shared/Foldout';
import Search from 'components/shared/Search';
import { colors } from 'styles/theme/colors';

type GroupRow = {
  name: string;
};

type Props = {
  groupNames: string[];
  userName: string;
  handleClose: () => void;
};

const columnHelper = createColumnHelper<GroupRow>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Group Name',
    cell: (data) => <BoldText>{data.getValue()}</BoldText>,
  }),
];

export default function UserGroupsSlideout({ groupNames, userName, handleClose }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    requestAnimationFrame(() => setOpen(true));
  }, []);

  const animateClose = () => {
    setOpen(false);
    setTimeout(handleClose, 300);
  };

  const groups: GroupRow[] = useMemo(() => groupNames.map((name) => ({ name })), [groupNames]);

  const filteredGroups = useMemo(() => {
    if (!search) return groups;
    const lower = search.toLowerCase();
    return groups.filter((g) => g.name.toLowerCase().includes(lower));
  }, [groups, search]);

  return (
    <Foldout open={open} position='right' onBackdropClick={animateClose} width='min(80%, 480px)'>
      <PanelContainer>
        <PanelHeader>
          <PanelTitle>{userName} - Groups</PanelTitle>
          <CloseButton onClick={animateClose} />
        </PanelHeader>
        <PanelContent>
          <ItemsCount>
            {filteredGroups.length} Group{filteredGroups.length !== 1 ? 's' : ''}
          </ItemsCount>
          <Search placeholder='Search groups' value={search} onChange={setSearch} />
          <DataTable columns={columns} data={filteredGroups} defaultSortBy={[{ id: 'name', desc: false }]} />
        </PanelContent>
        <PanelFooter>
          <ButtonGroup>
            <Button onClick={animateClose}>Close</Button>
          </ButtonGroup>
        </PanelFooter>
      </PanelContainer>
    </Foldout>
  );
}

const BoldText = styled.span`
  font-weight: 500;
`;

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 500;
  margin: 0;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PanelFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
  display: flex;
  justify-content: flex-end;
`;

const ItemsCount = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.black100};
`;
